import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";

const AppLoadingContext = createContext(null);

export function AppLoadingProvider({ children }) {
  const [activeCount, setActiveCount] = useState(0);
  const activeTasksRef = useRef(new Set());
  const taskIdRef = useRef(0);

  const beginTask = useCallback(() => {
    const taskId = ++taskIdRef.current;
    activeTasksRef.current.add(taskId);
    setActiveCount(activeTasksRef.current.size);

    let released = false;

    return () => {
      if (released) {
        return;
      }

      released = true;
      activeTasksRef.current.delete(taskId);
      setActiveCount(activeTasksRef.current.size);
    };
  }, []);

  const trackAsync = useCallback(
    (task) => {
      const endTask = beginTask();

      return Promise.resolve()
        .then(task)
        .finally(endTask);
    },
    [beginTask],
  );

  const value = useMemo(
    () => ({
      activeCount,
      isBlocking: activeCount > 0,
      beginTask,
      trackAsync,
    }),
    [activeCount, beginTask, trackAsync],
  );

  return <AppLoadingContext.Provider value={value}>{children}</AppLoadingContext.Provider>;
}

export function useAppLoading() {
  const context = useContext(AppLoadingContext);

  if (!context) {
    throw new Error("useAppLoading must be used within an AppLoadingProvider");
  }

  return context;
}
